<script setup>
import { ref, computed, onMounted } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Auth from '../../../../api/auth';
import wootConstants from 'dashboard/constants/globals';

const NXS_KEY = 'nxs_mail_r4k7p2w9x1m3';
const NXS_API = '/mail-api/nxs';

const props = defineProps({
  id: { type: Number, required: true },
  name: { type: String, required: true },
  email: { type: String, default: '' },
  type: { type: String, default: '' },
  availability: { type: String, default: '' },
  provider: { type: String, default: '' },
  customRoleId: { type: Number, default: null },
});

const emit = defineEmits(['close']);

const { AVAILABILITY_STATUS_KEYS } = wootConstants;

const store = useStore();
const { t } = useI18n();

const agentName = ref(props.name);
const agentAvailability = ref(props.availability);
const selectedRoleId = ref(props.customRoleId || props.type);
const agentCredentials = ref({ email: props.email });

const emailMode = ref('none');
const agentAccounts = ref([]);
const allAccounts = ref([]);
const emailDomains = ref([]);
const selectedAccountId = ref('');
const newEmailLocal = ref('');
const newEmailDomain = ref('');
const newEmailDisplayName = ref('');
const loadingEmail = ref(false);

const currentUser = useMapGetter('getCurrentUser');
const currentUserId = useMapGetter('getCurrentUserID');

const rules = {
  agentName: { required, minLength: minLength(1) },
  selectedRoleId: { required },
  agentAvailability: { required },
};

const v$ = useVuelidate(rules, {
  agentName,
  selectedRoleId,
  agentAvailability,
});

const pageTitle = computed(
  () => `${t('AGENT_MGMT.EDIT.TITLE')} - ${props.name}`
);

const uiFlags = useMapGetter('agents/getUIFlags');
const getCustomRoles = useMapGetter('customRole/getCustomRoles');

const roles = computed(() => {
  const defaultRoles = [
    {
      id: 'administrator',
      name: 'administrator',
      label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR'),
    },
    {
      id: 'agent',
      name: 'agent',
      label: t('AGENT_MGMT.AGENT_TYPES.AGENT'),
    },
  ];

  const customRoles = getCustomRoles.value.map(role => ({
    id: role.id,
    name: `custom_${role.id}`,
    label: role.name,
  }));

  return [...defaultRoles, ...customRoles];
});

const selectedRole = computed(() =>
  roles.value.find(
    role =>
      role.id === selectedRoleId.value || role.name === selectedRoleId.value
  )
);

const statusList = computed(() => {
  return [
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.ONLINE'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.BUSY'),
    t('PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.OFFLINE'),
  ];
});

const availabilityStatuses = computed(() =>
  statusList.value.map((statusLabel, index) => ({
    label: statusLabel,
    value: AVAILABILITY_STATUS_KEYS[index],
    disabled: props.availability === AVAILABILITY_STATUS_KEYS[index],
  }))
);

const availableToLink = computed(() => allAccounts.value);

function nxsHeaders() {
  return {
    'Content-Type': 'application/json',
    'X-NXS-Key': NXS_KEY,
    'X-NXS-Chatwoot-ID': String(currentUserId.value || ''),
    'X-NXS-Chatwoot-Name': currentUser.value?.name || 'Admin',
  };
}

async function loadEmailData() {
  loadingEmail.value = true;
  try {
    const [agentRes, allRes, domRes] = await Promise.all([
      fetch(NXS_API, {
        method: 'POST',
        headers: nxsHeaders(),
        body: JSON.stringify({
          action: 'admin_get_agent_accounts',
          target_chatwoot_id: props.id,
        }),
      }),
      fetch(NXS_API, {
        method: 'POST',
        headers: nxsHeaders(),
        body: JSON.stringify({ action: 'admin_list_all_accounts' }),
      }),
      fetch(NXS_API, {
        method: 'POST',
        headers: nxsHeaders(),
        body: JSON.stringify({ action: 'list_domains' }),
      }),
    ]);
    const agentData = await agentRes.json();
    const allData = await allRes.json();
    const domData = await domRes.json();
    if (Array.isArray(agentData)) agentAccounts.value = agentData;
    if (Array.isArray(allData)) allAccounts.value = allData;
    if (Array.isArray(domData)) emailDomains.value = domData;
  } catch { /* ignore */ }
  loadingEmail.value = false;
}

async function unlinkAccount(accountId) {
  await fetch(NXS_API, {
    method: 'POST',
    headers: nxsHeaders(),
    body: JSON.stringify({ action: 'admin_unlink_account', account_id: accountId }),
  });
  agentAccounts.value = agentAccounts.value.filter(a => a.id !== accountId);
  useAlert('Email desvinculado');
}

async function linkAccount() {
  if (!selectedAccountId.value) return;
  const res = await fetch(NXS_API, {
    method: 'POST',
    headers: nxsHeaders(),
    body: JSON.stringify({
      action: 'admin_link_account',
      account_id: selectedAccountId.value,
      target_chatwoot_id: props.id,
      target_name: agentName.value,
    }),
  });
  const data = await res.json();
  if (data.error) { useAlert(data.error); return; }
  agentAccounts.value.push(data);
  selectedAccountId.value = '';
  emailMode.value = 'none';
  useAlert('Email vinculado');
}

async function createAndLink() {
  if (!newEmailLocal.value || !newEmailDomain.value) return;
  const res = await fetch(NXS_API, {
    method: 'POST',
    headers: nxsHeaders(),
    body: JSON.stringify({
      action: 'admin_create_account_for',
      localPart: newEmailLocal.value,
      domainId: newEmailDomain.value,
      name: newEmailDisplayName.value || agentName.value,
      target_chatwoot_id: props.id,
      target_name: agentName.value,
    }),
  });
  const data = await res.json();
  if (data.error) { useAlert(data.error); return; }
  agentAccounts.value.push(data);
  newEmailLocal.value = '';
  newEmailDomain.value = '';
  newEmailDisplayName.value = '';
  emailMode.value = 'none';
  useAlert('Email criado e vinculado');
}

onMounted(() => { loadEmailData(); });

const editAgent = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  try {
    const payload = {
      id: props.id,
      name: agentName.value,
      availability: agentAvailability.value,
    };

    if (selectedRole.value.name.startsWith('custom_')) {
      payload.custom_role_id = selectedRole.value.id;
    } else {
      payload.role = selectedRole.value.name;
      payload.custom_role_id = null;
    }

    await store.dispatch('agents/update', payload);
    useAlert(t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'));
    emit('close');
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'));
  }
};

const resetPassword = async () => {
  try {
    await Auth.resetPassword(agentCredentials.value);
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('AGENT_MGMT.EDIT.PASSWORD_RESET.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header :header-title="pageTitle" />
    <form class="w-full" @submit.prevent="editAgent">
      <div class="w-full">
        <label :class="{ error: v$.agentName.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.NAME.LABEL') }}
          <input
            v-model="agentName"
            type="text"
            :placeholder="$t('AGENT_MGMT.EDIT.FORM.NAME.PLACEHOLDER')"
            @input="v$.agentName.$touch"
          />
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.selectedRoleId.$error }">
          {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.LABEL') }}
          <select v-model="selectedRoleId" @change="v$.selectedRoleId.$touch">
            <option v-for="role in roles" :key="role.id" :value="role.id">
              {{ role.label }}
            </option>
          </select>
          <span v-if="v$.selectedRoleId.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_TYPE.ERROR') }}
          </span>
        </label>
      </div>

      <div class="w-full">
        <label :class="{ error: v$.agentAvailability.$error }">
          {{ $t('PROFILE_SETTINGS.FORM.AVAILABILITY.LABEL') }}
          <select
            v-model="agentAvailability"
            @change="v$.agentAvailability.$touch"
          >
            <option
              v-for="status in availabilityStatuses"
              :key="status.value"
              :value="status.value"
            >
              {{ status.label }}
            </option>
          </select>
          <span v-if="v$.agentAvailability.$error" class="message">
            {{ $t('AGENT_MGMT.EDIT.FORM.AGENT_AVAILABILITY.ERROR') }}
          </span>
        </label>
      </div>

      <!-- Email Accounts Section -->
      <div class="w-full nxs-email-section">
        <div class="nxs-email-header">
          <span class="i-lucide-mail nxs-email-icon" />
          <span class="nxs-email-title">Contas de Email (servidor)</span>
        </div>

        <div v-if="loadingEmail" class="nxs-hint">Carregando...</div>

        <!-- Linked accounts -->
        <div v-for="acc in agentAccounts" :key="acc.id" class="nxs-account-row">
          <div class="nxs-account-badge">
            {{ acc.address.charAt(0).toUpperCase() }}
          </div>
          <div class="nxs-account-info">
            <span class="nxs-account-addr">{{ acc.address }}</span>
            <span class="nxs-account-name">{{ acc.name || '' }}</span>
          </div>
          <button type="button" class="nxs-unlink-btn" @click="unlinkAccount(acc.id)" title="Desvincular">
            <span class="i-lucide-x" />
          </button>
        </div>

        <div v-if="agentAccounts.length === 0 && !loadingEmail" class="nxs-hint" style="margin-bottom:8px">
          Nenhum email vinculado a este agente.
        </div>

        <!-- Add options -->
        <div class="nxs-email-options">
          <button type="button" class="nxs-option-btn" :class="{ active: emailMode === 'link' }" @click="emailMode = emailMode === 'link' ? 'none' : 'link'">
            <span class="i-lucide-link" /> Vincular existente
          </button>
          <button type="button" class="nxs-option-btn" :class="{ active: emailMode === 'create' }" @click="emailMode = emailMode === 'create' ? 'none' : 'create'">
            <span class="i-lucide-plus" /> Criar novo
          </button>
        </div>

        <!-- Link existing -->
        <div v-if="emailMode === 'link'" class="nxs-email-form">
          <label>
            Selecionar conta disponível
            <select v-model="selectedAccountId" class="nxs-select">
              <option value="">Selecione...</option>
              <option v-for="acc in availableToLink" :key="acc.id" :value="acc.id">
                {{ acc.address }} {{ acc.name ? `(${acc.name})` : '' }}
              </option>
            </select>
          </label>
          <button type="button" class="nxs-action-btn" :disabled="!selectedAccountId" @click="linkAccount">
            Vincular
          </button>
        </div>

        <!-- Create new -->
        <div v-if="emailMode === 'create'" class="nxs-email-form">
          <label>
            Nome de exibição
            <input v-model="newEmailDisplayName" type="text" placeholder="Nome no email" />
          </label>
          <label>
            Endereço
            <div class="nxs-email-addr-row">
              <input v-model="newEmailLocal" type="text" placeholder="usuario" class="nxs-input-local" />
              <span class="nxs-at">@</span>
              <select v-model="newEmailDomain" class="nxs-select-domain">
                <option value="">Domínio</option>
                <option v-for="d in emailDomains" :key="d.id" :value="d.id">{{ d.name }}</option>
              </select>
            </div>
          </label>
          <button type="button" class="nxs-action-btn" :disabled="!newEmailLocal || !newEmailDomain" @click="createAndLink">
            Criar e vincular
          </button>
        </div>
      </div>

      <div class="flex flex-row justify-start w-full gap-2 px-0 py-2">
        <div class="w-[50%] ltr:text-left rtl:text-right">
          <Button
            v-if="provider !== 'saml'"
            ghost
            type="button"
            icon="i-lucide-lock-keyhole"
            class="!px-2"
            :label="$t('AGENT_MGMT.EDIT.PASSWORD_RESET.ADMIN_RESET_BUTTON')"
            @click.prevent="resetPassword"
          />
        </div>
        <div class="w-[50%] flex justify-end items-center gap-2">
          <Button
            faded
            slate
            type="reset"
            :label="$t('AGENT_MGMT.EDIT.CANCEL_BUTTON_TEXT')"
            @click.prevent="emit('close')"
          />
          <Button
            type="submit"
            :label="$t('AGENT_MGMT.EDIT.FORM.SUBMIT')"
            :disabled="v$.$invalid || uiFlags.isUpdating"
            :is-loading="uiFlags.isUpdating"
          />
        </div>
      </div>
    </form>
  </div>
</template>

<style scoped>
.nxs-email-section {
  margin-top: 8px;
  margin-bottom: 8px;
  padding: 14px 16px;
  border: 1px solid rgb(var(--slate-4));
  border-radius: 10px;
  background: rgb(var(--slate-1));
}

.nxs-email-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.nxs-email-icon {
  font-size: 16px;
  color: rgb(var(--blue-9));
}

.nxs-email-title {
  font-size: 13px;
  font-weight: 700;
  color: rgb(var(--slate-12));
}

.nxs-account-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgb(var(--slate-4));
  margin-bottom: 8px;
}

.nxs-account-badge {
  width: 32px; height: 32px;
  border-radius: 50%;
  background: rgb(var(--blue-9));
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
}

.nxs-account-info {
  flex: 1;
  min-width: 0;
}

.nxs-account-addr {
  font-size: 13px;
  font-weight: 600;
  color: rgb(var(--slate-12));
  display: block;
}

.nxs-account-name {
  font-size: 12px;
  color: rgb(var(--slate-9));
}

.nxs-unlink-btn {
  width: 28px; height: 28px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: rgb(var(--slate-9));
  transition: all 0.15s;
}

.nxs-unlink-btn:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #dc2626;
}

.nxs-email-options {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.nxs-option-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border: 1px solid rgb(var(--slate-4));
  border-radius: 8px;
  background: rgb(var(--solid-1));
  font-size: 12px;
  font-weight: 600;
  color: rgb(var(--slate-11));
  cursor: pointer;
  transition: all 0.15s;
}

.nxs-option-btn:hover {
  border-color: rgb(var(--blue-9));
  color: rgb(var(--blue-9));
}

.nxs-option-btn.active {
  border-color: rgb(var(--blue-9));
  background: rgba(var(--blue-3), 0.3);
  color: rgb(var(--blue-9));
}

.nxs-email-form {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  background: rgb(var(--slate-2));
  margin-bottom: 8px;
}

.nxs-email-form label {
  font-size: 12px;
  font-weight: 600;
  color: rgb(var(--slate-12));
}

.nxs-email-addr-row {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
}

.nxs-input-local { flex: 1; }

.nxs-at {
  font-size: 14px;
  font-weight: 600;
  color: rgb(var(--slate-10));
}

.nxs-select-domain { flex: 1; }

.nxs-action-btn {
  align-self: flex-end;
  padding: 6px 16px;
  border: none;
  border-radius: 8px;
  background: rgb(var(--blue-9));
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: opacity 0.15s;
}

.nxs-action-btn:hover { opacity: 0.9; }
.nxs-action-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.nxs-hint {
  font-size: 12px;
  color: rgb(var(--slate-9));
  margin: 0;
}
</style>
